import React, { useCallback, useEffect, useMemo } from 'react'
import { AutoComplete, InputLabeledContainer, Select, SingleDatePicker } from 'tennders-common/form/components'
import TextInput from 'tennders-common/form/components/new-inputs/TextInput'
import { apiGet } from 'tennders-stracture/libs/api'
import FieldsetContainer from 'tennders-common/form/components/new-inputs/FieldsetContainer'
import useTranslation from 'next-translate/useTranslation'
import moment from 'moment/moment'
import MultipleSalesInvoiceFromCompany from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceFromCompany'
import MultipleSalesInvoiceToCompany from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceToCompany'
import { find, get } from 'lodash'
import SearchableSelect from 'tennders-common/form/components/new-inputs/SearchableSelect'

const dataExtractor = (data) => data.items.map((item) => ({ ...item, label: item.name }))
const requestBuilder = ({ keyword, options }) => apiGet(
  '/companies',
  {
    ...options,
    params: {
      query: keyword,
      attributes: '{id, name,preferences, paymentTerms{id, days, type{name},dateType,cmrType}}'
    }
  },
  true
)

const defaultItem = {
  label: 'Select',
  value: ''
}

const MultipleSalesInvoiceGeneralInfo = (props) => {
  const {
    form,
    setForm,
    formErrors,
    onCompanyChange,
    onChange,
    isCreditNote,
    selectedPaymentTerm,
    allPaymentTerms,
    company,
    getCompanySalesInvoicesRequest,
    companyInvoices
  } = props
  const { t } = useTranslation()

  const monthOptions = useMemo(() => {
    const pMonths = []
    const fMonth = Number(form?.month)
    const now = new Date()
    const previousValue = moment().subtract(1, 'months')
    const previousMonth = parseInt(previousValue.format('MM'), 10)
    if (!fMonth || fMonth !== previousMonth) {
      pMonths.push({
        value: previousMonth,
        label: moment().subtract(1, 'months').format('MMMM'),
        year: previousValue.year()
      })
    }
    if (fMonth) {
      pMonths.push({
        value: fMonth,
        label: moment(now.getFullYear() + '-' + fMonth + '-' + now.getDate()).format('MMMM'),
        year: now.getMonth() + 1 >= Number(fMonth) ? now.getFullYear() : now.getFullYear() - 1
      })
    }
    const currentMonth = parseInt(moment().format('MM'), 10)
    if (!fMonth || fMonth !== currentMonth) {
      pMonths.push({
        value: currentMonth,
        label: moment().format('MMMM'),
        year: now.getFullYear()
      })
    }
    return pMonths
  }, [form.month])

  const calculatesDueDate = useCallback((company, dateOfIssue, dispatchDate) => {
    const paymentTermDays = selectedPaymentTerm?.days || company.paymentTerm?.days
    if (typeof paymentTermDays !== 'undefined' && dateOfIssue) {
      const dueDate = moment(dispatchDate || dateOfIssue).add('days', paymentTermDays)
      if (!form.id) {
        onChange('dueDate', dueDate.set('hour', 17)
          .set('minute', 0)
          .set('second', 0).toDate())
      }
    }
  }, [selectedPaymentTerm?.days])

  useEffect(() => {
    if (company) {
      calculatesDueDate(company, form.date, form.dispatchDate)
    }
  }, [company, form.date, form.dispatchDate, form.paymentTerm])

  useEffect(() => {
    const month = parseInt(form.month, 10)
    const previousMonth = moment().subtract(1, 'months').endOf('month')
    const currentMonth = parseInt(moment().format('MM'), 10)
    const date = currentMonth === month ? moment().toDate() : previousMonth.toDate()
    const selectedYear = find(monthOptions, x => Number(x.value) === Number(form.month)) || monthOptions[0]
    let year
    if (selectedYear) {
      year = get(selectedYear, 'year')
    }
    setForm(prevForm => ({ ...prevForm, year, date }))
  }, [form?.month, form?.company, JSON.stringify(monthOptions)])

  return (
    <>
      <div className="d-flex row justify-space mt-20">
        <div className="w-30">
          <MultipleSalesInvoiceFromCompany
            toCompany={company}
            form={form}
            errors={formErrors}
            onChange={onChange}
            selectedPaymentTerm={selectedPaymentTerm}
            fromCompany={form?.fromCompany}
          />
        </div>
        <div className="w-30">
          <div>
            <div className="mb-10">
              <InputLabeledContainer
                name="company"
                form={form}
                label="Company Name"
                errors={formErrors}
                className="mb-10"
                onChange={(k, v) => onCompanyChange(v)}
                renderInput={(rest) => (
                  <AutoComplete
                    {...rest}
                    suggestionStyle={{ position: 'relative', top: 0 }}
                    requestBuilder={requestBuilder}
                    dataExporter={dataExtractor}
                    value={company ? company.name : form.company}
                    className="smaller form-input w-100"
                  />
                )}
              />
            </div>
            {isCreditNote && (
              <div className="mb-10">
                <FieldsetContainer
                  name="salesInvoiceId"
                  label={t('salesInvoice:salesInvoiceTitle')}
                  form={form}
                  errors={formErrors}
                  onChange={onChange}
                  disabled={form.id}
                  renderInput={(props) => (
                    <SearchableSelect
                      fetchItems={company && getCompanySalesInvoicesRequest}
                      options={companyInvoices}
                      labelKey="label"
                      valueKey="id"
                      label={t('salesInvoice:salesInvoiceTitle')}
                      {...props}
                    />
                  )}
                />
              </div>
            )}
            <div className="mb-10">
              <FieldsetContainer
                name="year"
                label={t('salesInvoice:year')}
                form={form}
                errors={formErrors}
                onChange={onChange}
                renderInput={(props) => (
                  <TextInput
                    className="smaller form-select w-100"
                    readOnly
                    {...props}
                  />
                )}
              />
            </div>
            <div className="mb-10">
              <FieldsetContainer
                name="month"
                label={t('salesInvoice:month')}
                form={form}
                errors={formErrors}
                onChange={onChange}
                renderInput={(props) => (
                  <Select
                    className="smaller form-select w-100"
                    items={monthOptions}
                    defaultItem={monthOptions ? false : defaultItem}
                    {...props}
                  />
                )}
              />
            </div>
            {isCreditNote
              ? (
                <div className="mb-10">
                  <InputLabeledContainer
                    name="date"
                    label={t('creditNote:date')}
                    form={form}
                    errors={formErrors}
                    onChange={onChange}
                    required
                    disabled
                    renderInput={(props) => <SingleDatePicker className="smaller form-input w-100" {...props} />}
                  />
                </div>
              )
              : (
                <>
                  <InputLabeledContainer
                    label="Payment Term"
                    form={form}
                    name="paymentTerm"
                    className="mb-10"
                    errors={formErrors}
                    onChange={onChange}
                    renderInput={(props) => (
                      <Select
                        items={allPaymentTerms}
                        className="form-select small"
                        {...props}
                      />
                    )}
                  />
                  <div className="mb-10">
                    <InputLabeledContainer
                      name="date"
                      label={t('salesInvoice:dateOfIssue')}
                      form={form}
                      errors={formErrors}
                      onChange={onChange}
                      renderInput={(props) => <SingleDatePicker readOnly className="smaller form-input w-100" {...props} />}
                    />
                  </div>
                  <div className="mb-10">
                    <InputLabeledContainer
                      name="dispatchDate"
                      label={t('salesInvoice:dispatchDate')}
                      form={form}
                      errors={formErrors}
                      onChange={onChange}
                      required
                      renderInput={(props) => <SingleDatePicker className="smaller form-input w-100" {...props} />}
                    />
                  </div>
                </>
              )}
            <div className="mb-10">
              <InputLabeledContainer
                name="dueDate"
                label={t('salesInvoice:dueDate')}
                form={form}
                errors={formErrors}
                onChange={onChange}
                required
                renderInput={(props) => <SingleDatePicker className="smaller form-input w-100" {...props} />}
              />
            </div>
          </div>
        </div>
        <div className="w-30">
          <MultipleSalesInvoiceToCompany toCompany={form?.toCompany} />
        </div>
      </div>
    </>
  )
}

export default React.memo(MultipleSalesInvoiceGeneralInfo)
